import type { Boss, BossSpriteRuntime } from './types'
import { getKnightResetMode } from './boss_pose_timeline'

// 既定 = 'more' (2026-10-12 ユーザー採用・配布版): 技の後、足をそろえて立つ前に足を寄せる絵を120ms挟む (2026-10-08 ユーザー採用 V-1「足を寄せる絵を挟む」= mid)。
//   それに加えて、押し返し・石突・崩れの後にも同じ絵を挟む (試遊パック 10-10 の答え F-1「3つにも挟む」)。
// 旧は DEV の URL (game.ts の DEV ブロックが読む) で ?knightRest=mid (旧 = 押し返し・石突・崩れの後には挟まない) / ?knightRest=now (足をそろえて立つ) / ?knightRest=reach (広い構えで止まる)。
export type KnightRestMode = 'mid' | 'now' | 'reach' | 'more'
let restMode: KnightRestMode = 'more'
export const KNIGHT_REST_MID_MS = 120
// 崩れの後だけ2段 (比較版 ?knightRest=more): rest_from_stagger を80ms → rest_from_stagger2 を80ms → idle。兜が1コマに50px以上跳ばないように。
export const KNIGHT_REST_STAGGER_STEP_MS = 80
// 構え直しの後だけ3段 (2026-10-27 配布版): rest_from_rearm_a → rest_from_rearm → rest_from_rearm_b を50msずつ → idle。
//   構え直し→足を寄せる絵→立ち姿の2回の切り替えで後ろ足が1コマに 44px・37px 跳んでいたのを、間の絵2枚で4回に割る (docs/research/2026-10-27-knight-rearm-foot-jump.md)。
//   旧 (rest_from_rearm を120msだけ) は DEV の ?knightRearmTween=off。
export const KNIGHT_REARM_STEP_MS = 50
const REARM_STEPS = ['rest_from_rearm_a', 'rest_from_rearm', 'rest_from_rearm_b'] as const
let rearmTween = true
export function setKnightRearmTween(value: string | null): void {
  rearmTween = value !== 'off'
}
const rearmTotalMs = (): number => rearmTween ? KNIGHT_REARM_STEP_MS * REARM_STEPS.length : KNIGHT_REST_MID_MS
const rearmStepAt = (elapsedMs: number): KnightRestMidKey => rearmTween
  ? REARM_STEPS[Math.min(REARM_STEPS.length - 1, Math.max(0, Math.floor(elapsedMs / KNIGHT_REARM_STEP_MS)))] : 'rest_from_rearm'
export type KnightRestMidKey = 'rest_from_scrape' | 'rest_from_rearm' | 'rest_from_thrust' | 'rest_from_butt' | 'rest_from_stagger' | 'rest_from_stagger2'
  | 'rest_from_rearm_a' | 'rest_from_rearm_b'
export function setKnightRest(value: string | null): void {
  restMode = value === 'now' || value === 'reach' || value === 'mid' ? value : 'more'
}
const midOn = (): boolean => restMode === 'mid' || restMode === 'more'
export function knightRestPoseKey(boss: Boss): 'walk_reach' | null {
  return restMode === 'reach' && boss.id === 'vigil-knight' && boss.hp > 0
    && boss.state === 'idle' && !boss.knightGait?.walking ? 'walk_reach' : null
}

// 最後の recovery の絵 → 足を寄せる絵 (idle に入った時の拍と、telegraph の頭の拍で共通)。
function restKeyForImage(img: string | undefined, more: boolean): KnightRestMidKey | undefined {
  return img === 'scrape_stop' ? 'rest_from_scrape'
    : img === 'lanceSweep_rearm' ? 'rest_from_rearm'
    : img === 'lanceThrust_recover' ? 'rest_from_thrust'
    : more && img === 'lanceButt_slam' ? 'rest_from_butt'
    : more && img === 'staggered' ? 'rest_from_stagger' : undefined
}
const restMsFor = (key: KnightRestMidKey | undefined): number =>
  key === 'rest_from_stagger' ? KNIGHT_REST_STAGGER_STEP_MS * 2 : key === 'rest_from_rearm' ? rearmTotalMs() : key ? KNIGHT_REST_MID_MS : 0
const restStep2 = (key: KnightRestMidKey | undefined, remainingMs: number): KnightRestMidKey | null =>
  key === 'rest_from_stagger' && remainingMs <= KNIGHT_REST_STAGGER_STEP_MS ? 'rest_from_stagger2'
    : key === 'rest_from_rearm' ? rearmStepAt(rearmTotalMs() - remainingMs) : key ?? null

/** Draw-only history: remember the actual recovery bitmap, not generic active.
 *  telegraphImage = state が telegraph の時の、その tick の時間軸の絵 (recovery から idle を通らず telegraph へ移る回の拍に使う)。 */
export function advanceKnightRestMid(ss: BossSpriteRuntime, boss: Boss, recoveryImage: string | null, dt: number, telegraphImage?: string | null): Partial<BossSpriteRuntime> {
  if (!midOn() || boss.id !== 'vigil-knight') return {}
  const more = restMode === 'more'
  // 崩れ (staggered) は recovery ではなく state が直接 idle へ戻る。絵は state キーの 'staggered'。
  const last = boss.state === 'recovery' ? recoveryImage : more && boss.state === 'staggered' && boss.hp > 0 ? 'staggered' : null
  let key = ss.restMidKey, remaining = Math.max(0, (ss.restMidRemainingMs ?? 0) - dt * 1000)
  if (boss.state === 'idle' && ss.restMidLastRecoveryImage) {
    key = restKeyForImage(ss.restMidLastRecoveryImage, more)
    remaining = restMsFor(key)
  }
  // Consume entry even if walking starts immediately; it cannot reappear at a stop.
  if (boss.state !== 'idle' || boss.knightGait?.walking || boss.hp <= 0 || Math.abs(boss.x - ss.prevX) > 0.02) {
    key = undefined; remaining = 0
  }
  // recovery (または崩れ) から idle を通らず直接 telegraph へ移る回 (例: 壁ぎわの突進 → 薙ぎ): telegraph の最初の絵が idle (直立) なので
  //   低い構え → 直立が1コマで跳ぶ (10-08 実測 足 79px・兜 67px)。その idle の絵の間だけ、最後の recovery の絵に応じた中間の絵を最初の 120ms 描く。
  //   telegraph の絵が idle 以外 (構え) に替わったら即やめる (予兆の構えは隠さない)。idle 経由の拍と同じ tick には両方立たない (state が違う)。
  let teleKey = ss.restMidTeleKey, teleRemaining = Math.max(0, (ss.restMidTeleRemainingMs ?? 0) - dt * 1000)
  if (boss.state === 'telegraph' && ss.restMidLastRecoveryImage && boss.hp > 0) {
    teleKey = restKeyForImage(ss.restMidLastRecoveryImage, more)
    teleRemaining = restMsFor(teleKey)
  }
  if (boss.state !== 'telegraph' || boss.hp <= 0 || (telegraphImage != null && telegraphImage !== 'idle')) {
    teleKey = undefined; teleRemaining = 0
  }
  return { restMidLastRecoveryImage: last ?? undefined,
    restMidKey: remaining > 0 ? key : undefined, restMidRemainingMs: remaining,
    restMidTeleKey: teleRemaining > 0 ? teleKey : undefined, restMidTeleRemainingMs: teleRemaining }
}

export function knightRestMidPoseKey(boss: Boss, ss: BossSpriteRuntime, playing: boolean): KnightRestMidKey | null {
  return midOn() && playing && boss.id === 'vigil-knight' && boss.hp > 0
    && boss.state === 'idle' && !boss.knightGait?.walking && (ss.restMidRemainingMs ?? 0) > 0
    ? restStep2(ss.restMidKey, ss.restMidRemainingMs!) : null
}

/** telegraph の頭 (recovery から idle を通らず来た回): 時間軸の絵が idle の間だけ、最初の 120ms を中間の絵へ差し替える (描画のみ)。 */
export function knightRestMidTeleKey(boss: Boss, ss: BossSpriteRuntime, timelineImage: string | null | undefined, playing: boolean): KnightRestMidKey | null {
  return midOn() && playing && boss.id === 'vigil-knight' && boss.hp > 0 && boss.state === 'telegraph'
    && timelineImage === 'idle' && (ss.restMidTeleRemainingMs ?? 0) > 0
    ? restStep2(ss.restMidTeleKey, ss.restMidTeleRemainingMs!) : null
}

// 壁ぎわの突進 (と切り下がり) の recovery の途中で向きが変わる (turn の絵の拍) と、低い構え (scrape_stop・右向き) → 直立の turn → 低い構え (左向き)
//   と低い→直立→低いが入れ替わる (10-08 実戦の撮影)。既定 = stand (2026-10-29 作者「一旦待機の絵で…あまりにも絵が飛んでるようだったら、間に自然な絵を追加」):
//   turn の拍が終わった後の scrape_stop を待機の絵 (idle) に替える (turn の直前 120ms も)。描画のみ。
//   待機の絵だけだと低い構え → 待機が1コマで兜 103px 跳んだので、その前に構え直しの間の絵2枚 (rest_from_rearm_a → b・50ms ずつ) を挟んで立ち上がらせる。
//   既定 = plant (2026-10-29 作者「柄の方の軌道がいきなり飛んじゃってる」→ 比較の動画に「ok」で配布版へ。前の stand は DEV ?knightScrapeTurn=stand): 構え直しの2枚は槍の石突が前→後ろ→前と飛ぶので、
//     替わりに石突を後ろから前へ順に運ぶ4枚 (rest_from_scrape → rest_rise_55 → rest_rise_70 → rest_from_butt・50ms ずつ) で立ち上がる。
//     中の2枚は Codex 10-29 の中割り (記録 docs/research/2026-10-29-knight-turn-butt-and-shade-return.md)。
//   間の絵なしは DEV の ?knightScrapeTurn=idle、前の配布版 (半分起きた構え rest_from_scrape・作者「ダサい」) は ?knightScrapeTurn=rest、低い構えのままは ?knightScrapeTurn=now。
export type KnightScrapeTurnMode = 'now' | 'rest' | 'idle' | 'stand' | 'plant'
let scrapeTurnMode: KnightScrapeTurnMode = 'plant'
export function setKnightScrapeTurn(value: string | null): void {
  scrapeTurnMode = value === 'now' || value === 'rest' || value === 'idle' || value === 'stand' ? value : 'plant'
}
/** recovery の途中で turn の絵が出たかを覚える (recovery を出たら消す)。 */
export function advanceKnightScrapeTurn(ss: BossSpriteRuntime, boss: Boss, recoveryImage: string | null): Partial<BossSpriteRuntime> {
  if (scrapeTurnMode === 'now' || boss.id !== 'vigil-knight') return ss.scrapeTurnSeen ? { scrapeTurnSeen: undefined } : {}
  const seen = boss.state === 'recovery' && (ss.scrapeTurnSeen === true || recoveryImage === 'turn')
  return { scrapeTurnSeen: seen || undefined }
}
// turn の拍に入る直前の 120ms (時間軸の turn の開始の 120ms 前) の scrape_stop も同じ絵へ替える (向きは turn 前のまま・描画のみ)。
//   scrape_stop (低い構え) → turn (直立) の1コマの跳び (前足 90px・兜 73px) を和らげる。
//   並び (stand) = scrape_stop → rest_from_rearm_a → rest_from_rearm_b → idle → turn → idle → …
export const KNIGHT_SCRAPE_TURN_AT = (327 + 300) / 1550 // boss_pose_timeline.ts knightWallCharge の recovery 'turn' の at と同じ (recoveryMs 1550 の 627ms)
export const KNIGHT_SCRAPE_TURN_LEAD_MS = 120
export const KNIGHT_SCRAPE_RISE_STEP_MS = 50
const SCRAPE_RISE_STEPS = ['rest_from_rearm_a', 'rest_from_rearm_b'] as const
const SCRAPE_PLANT_STEPS = ['rest_from_scrape', 'rest_rise_55', 'rest_rise_70', 'rest_from_butt'] as const
export type KnightScrapeTurnKey = 'rest_from_scrape' | 'idle' | typeof SCRAPE_RISE_STEPS[number] | typeof SCRAPE_PLANT_STEPS[number]
/** turn の拍が終わった後と turn の直前 120ms の scrape_stop の替わり先 (stand・idle = idle・rest = rest_from_scrape)・それ以外は null。
 *  stand はその 120ms のさらに前の 100ms を立ち上がる間の絵2枚に、plant は前の 200ms を石突を前へ運ぶ4枚にする。 */
export function knightScrapeTurnKey(boss: Boss, ss: BossSpriteRuntime, timelineImage: string | null | undefined): KnightScrapeTurnKey | null {
  if (scrapeTurnMode === 'now' || boss.id !== 'vigil-knight' || boss.hp <= 0 || boss.state !== 'recovery'
    || timelineImage !== 'scrape_stop') return null
  if (!ss.scrapeTurnSeen) {
    if (boss.currentAttack?.id !== 'knightWallCharge') return null
    const turnAtMs = KNIGHT_SCRAPE_TURN_AT * boss.currentAttack.recoveryMs
    const ms = boss.stateElapsed * 1000
    if (ms >= turnAtMs) return null
    const beforeIdleMs = turnAtMs - KNIGHT_SCRAPE_TURN_LEAD_MS - ms // >0 = 待機の絵に替わるより前
    if (beforeIdleMs > 0) {
      const steps = scrapeTurnMode === 'stand' ? SCRAPE_RISE_STEPS : scrapeTurnMode === 'plant' ? SCRAPE_PLANT_STEPS : null
      if (!steps) return null
      const step = steps.length - Math.ceil(beforeIdleMs / KNIGHT_SCRAPE_RISE_STEP_MS)
      return step >= 0 ? steps[step]! : null
    }
  }
  return scrapeTurnMode === 'rest' ? 'rest_from_scrape' : 'idle'
}
/** stand のときは直立を描くので、recovery の最後の絵 (idle 後の足を寄せる拍の元) も idle として覚える。 */
export function knightScrapeTurnHistoryImage(boss: Boss, ss: BossSpriteRuntime, recoveryImage: string | null): string | null {
  return scrapeTurnMode === 'stand' || scrapeTurnMode === 'idle' || scrapeTurnMode === 'plant' ? (knightScrapeTurnKey(boss, ss, recoveryImage) ?? recoveryImage) : recoveryImage
}

// 柄の押し返し (lanceShove) は recovery の途中 (0.6) で絵が idle に替わる = state は recovery のまま。
// そのため idle state 経由ではなく、その idle の絵が出る最初の 120ms だけ差し替える (描画のみ・既定の more。?knightRest=mid/now/reach では出ない)。
export const KNIGHT_SHOVE_IDLE_AT = 0.6 // boss_pose_timeline.ts lanceShove の recovery 'idle' の at と同じ
export function knightRestMidShoveKey(boss: Boss, timelineImage: string | null | undefined): 'rest_from_shove' | null {
  if (restMode !== 'more' || boss.id !== 'vigil-knight' || boss.hp <= 0 || boss.state !== 'recovery'
    || boss.currentAttack?.id !== 'lanceShove' || timelineImage !== 'idle') return null
  const sinceMs = boss.stateElapsed * 1000 - boss.currentAttack.recoveryMs * KNIGHT_SHOVE_IDLE_AT
  return sinceMs >= 0 && sinceMs < KNIGHT_REST_MID_MS ? 'rest_from_shove' : null
}

// 薙ぎ (lanceSweep) の recovery が 640ms を超えて続いた回 (体勢の揺れ・遅れで 880〜930ms まで続く) は、640ms 以降の絵が
// 構え直し (lanceSweep_rearm) から idle へ替わる = state は recovery のまま・85px の跳びがそのまま出ていた (10-08 実測 28回中7回)。
// そのため押し返しと同じく、その idle の絵に替わる最初の 150ms (上の3段・旧 120ms) だけ rest_from_rearm へ差し替える (描画のみ・既定 more と mid で出る・now/reach では出ない)。
// recovery の最後の絵が idle なら advanceKnightRestMid は idle 経由の拍を始めない = 二重にならない。
export const KNIGHT_SWEEP_IDLE_AT_MS = 640 // boss_pose_timeline.ts knightResetRecoveryImage の 'idle' に替わる時刻と同じ
export function knightRestMidRearmKey(boss: Boss, timelineImage: string | null | undefined): KnightRestMidKey | null {
  if (!midOn() || boss.id !== 'vigil-knight' || boss.hp <= 0 || boss.state !== 'recovery'
    || boss.currentAttack?.id !== 'lanceSweep' || timelineImage !== 'idle') return null
  const resetMode = getKnightResetMode()
  if (resetMode !== 'on' && resetMode !== 'single') return null // rearm の絵を出さない設定 (off / prev) では足を寄せない
  const sinceMs = boss.stateElapsed * 1000 - KNIGHT_SWEEP_IDLE_AT_MS
  return sinceMs >= 0 && sinceMs < rearmTotalMs() ? rearmStepAt(sinceMs) : null
}
